import { FormTheme } from '@app/constants/theme';
import useFormContentLanguage from '@app/lib/hooks/use-form-content-language';
import { StandardFormFieldDto } from '@app/models/dtos/form';
import { AppInput } from '@app/shadcn/components/ui/input';
import { cn } from '@app/shadcn/util/lib';
import { activeFieldAtom } from '@app/store/jotai/active-builder-component';
import { useBuilderActions } from '@app/store/jotai/field-selectors';
import { IThemeState, useFormState } from '@app/store/jotai/form';
import { useFormResponse } from '@app/store/jotai/responder-form-response';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { useAtomValue } from 'jotai';
import { Check, Circle, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDebounceValue } from 'usehooks-ts';
import { useTranslation } from 'react-i18next';

interface IMatrixFieldProps {
    field: StandardFormFieldDto;
    disabled?: boolean;
}

function MatrixFieldComponent({ field, disabled }: IMatrixFieldProps) {
    const { t } = useTranslation('builder');
    const { theme } = useFormState();

    const { addFieldChoicesAnswer, addFieldChoiceAnswer, formResponse, removeAnswer } = useFormResponse();

    const borderColor = theme?.secondary;
    const bgColor = theme?.tertiary;

    const { updateRowTitle, updateColumnTitle, deleteColumn, deleteRow } = useBuilderActions();
    const rowId = (index: number) => `matrix-${field.id}-row-${index}`;
    const columnId = (index: number) => `matrix-${field.id}-col-${index}`;
    const activeField = useAtomValue(activeFieldAtom);
    // Row and column titles are the author's text: marked with the form's
    // language when it is not the page's (the "Row 1" fallbacks are not).
    const contentLang = useFormContentLanguage();

    return (
        <div className="flex w-full flex-col overflow-x-auto pb-2">
            <div
                className="grid"
                style={{
                    gridTemplateColumns: `repeat(${(field?.properties?.fields?.[0]?.properties?.choices?.length || 0) + 1} ,minmax(120px,1fr))`
                }}
            >
                <div
                    className="sticky left-0 z-10 -mt-1 w-full opacity-100"
                    style={{
                        background: theme?.accent
                    }}
                ></div>
                {(field?.properties?.fields?.length || -1) > 0 &&
                    field?.properties?.fields?.[0]?.properties?.choices?.map((choice, index) => {
                        return (
                            <div
                                className={cn(
                                    'relative flex max-h-[150px] w-full flex-col items-center justify-center overflow-auto border border-t-[2px] p-2',
                                    index === 0 && 'rounded-tl-lg border-l-[2px]',
                                    field?.properties?.fields?.[0]?.properties?.choices?.length === index + 1 && 'rounded-tr-lg border-r-[2px]'
                                )}
                                key={choice?.id}
                                id={columnId(index)}
                                style={{
                                    borderColor: borderColor,
                                    background: bgColor + '55',
                                    color: borderColor
                                }}
                            >
                                {disabled ? (
                                    <MatrixHeaderInput
                                        disabled={!disabled}
                                        value={choice?.value ?? `Column ${index + 1}`}
                                        placeholder={t('CANVAS.COLUMN_N', { n: index + 1 })}
                                        onChange={(value: string) => {
                                            if (field.id === activeField?.id) updateColumnTitle(index, value);
                                        }}
                                    />
                                ) : (
                                    <div className="max-h-full max-w-full wrap-break-word text-center text-sm" lang={choice?.value ? contentLang : undefined}>
                                        {choice?.value || `Column ${index + 1}`}
                                    </div>
                                )}

                                {disabled && activeField?.id === field.id && (
                                    <button
                                        type="button"
                                        aria-label={t('CANVAS.REMOVE_COLUMN', { n: index + 1 })}
                                        className="cross-top absolute left-1 h-6 w-6 rounded-full bg-white p-1 opacity-50 focus-visible:opacity-100"
                                        onClick={() => {
                                            deleteColumn(index);
                                        }}
                                    >
                                        <X className="h-4 w-4" aria-hidden="true" />
                                    </button>
                                )}
                            </div>
                        );
                    })}
            </div>

            {field?.properties?.fields?.map((row: StandardFormFieldDto, index) => {
                // Each row is a group named by its row title (radio buttons, or
                // checkboxes when several answers are allowed); each cell is
                // named by its column title.
                const rowProps = {
                    className: 'grid w-fit min-w-full',
                    style: { gridTemplateColumns: `repeat(${(field?.properties?.fields?.[0]?.properties?.choices?.length || 0) + 1} ,minmax(120px,1fr))` },
                    'aria-labelledby': rowId(index)
                };
                const header = (
                    <div className="sticky left-0" style={{ background: theme?.accent }}>
                        <div
                            id={rowId(index)}
                            style={{
                                borderColor: borderColor,
                                background: bgColor + '55',
                                color: borderColor,
                                opacity: 1
                            }}
                            className={cn(
                                'relative z-10 flex max-h-[150px] w-full flex-col items-center justify-center overflow-auto border border-l-[2px] p-2',
                                index === 0 && 'rounded-tl-lg border-t-[2px]',
                                field?.properties?.fields?.length === index + 1 && 'rounded-bl-lg border-b-[2px]'
                            )}
                        >
                            {disabled ? (
                                <MatrixHeaderInput
                                    disabled={!disabled}
                                    value={row?.title?.toString() ?? `Row ${index + 1}`}
                                    placeholder={t('CANVAS.ROW_N', { n: index + 1 })}
                                    onChange={(value: string) => {
                                        if (activeField?.id === field.id) updateRowTitle(index, value);
                                    }}
                                />
                            ) : (
                                <div className="max-h-full max-w-full wrap-break-word text-center text-sm" lang={row?.title?.toString() ? contentLang : undefined}>
                                    {row?.title?.toString() || `Row ${index + 1}`}
                                </div>
                            )}
                            {disabled && activeField?.id === field.id && (field?.properties?.fields?.length || -1) > 1 && (
                                <button
                                    type="button"
                                    aria-label={t('CANVAS.REMOVE_ROW', { n: index + 1 })}
                                    className="cross-top absolute left-1 h-6 w-6 rounded-full bg-white p-1 opacity-50 focus-visible:opacity-100"
                                    onClick={() => {
                                        deleteRow(index);
                                    }}
                                >
                                    <X className="h-4 w-4" aria-hidden="true" />
                                </button>
                            )}
                        </div>
                    </div>
                );
                const cells = (
                    <>
                        {row?.properties?.choices?.map((choice: any, index: number) => {
                            return (
                                <div
                                    style={{
                                        borderColor: borderColor
                                    }}
                                    className={cn(
                                        'flex h-full  flex-col items-center justify-center border border-gray-200',
                                        index + 1 === row?.properties?.choices?.length && 'border-r-[2px]',
                                        row!.index + 1 === field?.properties?.fields?.length && 'border-b-[2px]'
                                    )}
                                    key={index}
                                >
                                    {field.properties?.allowMultipleSelection ? (
                                        <MatrixCheckbox
                                            checked={formResponse.answers[row.id]?.choices?.values?.includes(choice?.id)}
                                            onCheckedChange={(checked) => {
                                                let choices;
                                                if (checked) {
                                                    choices = formResponse.answers[row.id]?.choices?.values || [];
                                                    choices.push(choice.id);
                                                } else {
                                                    choices = (formResponse.answers[row.id]?.choices?.values || []).filter((choiceId) => choiceId !== choice.id);
                                                }
                                                addFieldChoicesAnswer(row.id, choices);
                                            }}
                                            theme={theme}
                                            disabled={disabled}
                                            aria-labelledby={columnId(index)}
                                        />
                                    ) : (
                                        <RadioGroupItem value={choice.id} theme={theme} className="h-full" disabled={disabled} aria-labelledby={columnId(index)} />
                                    )}
                                </div>
                            );
                        })}
                    </>
                );
                return field.properties?.allowMultipleSelection ? (
                    <div role="group" key={row?.id} {...rowProps}>
                        {header}
                        {cells}
                    </div>
                ) : (
                    <RadioGroup
                        key={row?.id}
                        {...rowProps}
                        onValueChange={(value) => {
                            addFieldChoiceAnswer(row.id, value);
                        }}
                        value={formResponse?.answers[row.id]?.choice?.value}
                    >
                        {header}
                        {cells}
                    </RadioGroup>
                );
            })}
        </div>
    );
}

const MatrixHeaderInput = ({ value, onChange, disabled, placeholder }: { value: string; onChange?: (value: string) => void; disabled?: boolean; placeholder?: string }) => {
    const { t } = useTranslation('builder');
    const [inputVal, setInputVal] = useState(value);
    const { theme } = useFormState();

    const [debouncedInputValue] = useDebounceValue(inputVal, 300);
    useEffect(() => {
        onChange && onChange(debouncedInputValue);
    }, [debouncedInputValue]);

    useEffect(() => {
        if (value !== inputVal) setInputVal(value);
    }, [value]);

    return (
        <StyledMatrixHeaderInput
            $theme={theme}
            className={cn('ring-none focus:ring-none items-center border-none bg-transparent text-center text-sm outline-hidden focus:border-none! focus:outline-hidden focus-visible:outline-hidden active:outline-hidden', disabled && 'pointer-events-none')}
            placeholder={placeholder || t('CANVAS.HEADER')}
            value={inputVal}
            onChange={(event) => {
                if (!disabled) setInputVal(event.target.value);
            }}
        />
    );
};
const StyledMatrixHeaderInput = styled(AppInput)<{ $theme?: IThemeState }>(({ $theme }) => {
    // Theme via prop; avoid calling useFormState() inside the styled interpolation.
    const themeColor = $theme?.tertiary;
    const secondaryColor = $theme?.secondary;
    return {
        resize: 'none',
        overflow: 'auto',
        width: '100%',
        borderRadius: '6px',
        '&::placeholder': {
            color: secondaryColor
        },
        '&:focus::placeholder': {
            color: themeColor
        }
    };
});

type MatrixCheckboxProps = React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & {
    className?: string;
    theme?: FormTheme; // or whatever type your theme should be
};

const MatrixCheckbox = React.forwardRef<React.ElementRef<typeof CheckboxPrimitive.Root>, MatrixCheckboxProps>(({ className, theme, ...props }, ref) => (
    <CheckboxPrimitive.Root
        ref={ref}
        style={{
            borderColor: theme?.secondary
        }}
        className={cn('h-5 w-5 rounded border focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed', className)}
        {...props}
    >
        <CheckboxPrimitive.Indicator
            style={{
                background: theme?.secondary
            }}
            className={cn(' flex h-5 w-5 items-center justify-center rounded')}
        >
            <Check className="text-white" />
        </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
));
MatrixCheckbox.displayName = CheckboxPrimitive.Root.displayName;

type RadioGroupProps = React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root> & {
    className?: string;
    theme?: FormTheme; // or whatever type your theme should be
};

const RadioGroup = React.forwardRef<React.ElementRef<typeof RadioGroupPrimitive.Root>, RadioGroupProps>(({ className, theme, ...props }, ref) => {
    return <RadioGroupPrimitive.Root className={cn('', className)} {...props} ref={ref} />;
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

type RadioGroupItemProps = React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> & {
    className?: string;
    theme?: FormTheme; // or whatever type your theme should be
};

const RadioGroupItem = React.forwardRef<React.ElementRef<typeof RadioGroupPrimitive.Item>, RadioGroupItemProps>(({ className, theme, ...props }, ref) => {
    return (
        <RadioGroupPrimitive.Item
            ref={ref}
            className={cn(
                'text-primary border-primary ring-offset-background focus-visible:ring-ring flex aspect-square h-7! w-7! flex-col items-center justify-center rounded-full border! focus:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed',
                className
            )}
            {...props}
            style={{
                border: `solid` + theme?.secondary || '' // Assuming theme.secondary represents the background color
            }}
        >
            <RadioGroupPrimitive.Indicator
                style={{
                    color: theme?.secondary // Assuming theme.secondary represents the background color
                }}
                className={cn('flex items-center justify-center')}
            >
                <Circle className="h-5 w-5 fill-current text-current" />
            </RadioGroupPrimitive.Indicator>
        </RadioGroupPrimitive.Item>
    );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

const MatrixField = React.memo(MatrixFieldComponent, (prev, next) => {
    return prev.field === next.field;
});
export default MatrixField;
